import React, { useMemo, useState } from 'react';
import '../styles/CustomerFollowUp.scss';
import useOneWayIn from '../../hooks/use-one-way-in';
import { useParams } from 'react-router-dom';
import createOwiUpdateDto from '../utils/createOwiUpdateDto';
import { PageParams } from '../types/PageParams';
import DataTableV3, { DataTableV3Actions } from 'src/components/DataTableV3';
import { DataTableV3Ids } from 'src/constants/dataTableIds';
import { useServiceColumns } from './useServiceColumns';
import { useCluster } from 'src/features/cluster';
import useOneWayInServices from '../../hooks/use-one-way-in-services';
import { useTranslation } from 'react-i18next';
import { OneWayInOpportunityStatusCreateDto, OneWayInServiceDto, OneWayInStatus } from 'src/api/v2';
import RecommendServicesModal from '../../opportunity-board/components/RecommendServicesModal';
import { toast } from 'react-toastify';
import { useUser } from 'src/features/authorization';

import { OneWayInOpportunityDto, OneWayInOpportunitySimpleV2Dto } from 'src/api/v2';
import DeliverServicesModal from '../../opportunity-board/components/DeliverServicesModal';

// Mapping function from OneWayInOpportunityDto to OneWayInOpportunitySimpleV2Dto
export function mapOpportunityDtoToSimpleV2Dto(dto: OneWayInOpportunityDto): OneWayInOpportunitySimpleV2Dto {
  return {
    clusterId: dto.clusterId,
    id: dto.id,
    title: dto.title,
    oneWayInId: dto.oneWayInId,
    isClosed: dto.isClosed,
    municipality: dto.municipality,
    customerName: dto.customerName,
    organizationName: dto.organizationName,
    organizationNumber: dto.organizationNumber,
    submittedOn: dto.submittedOn,
    lastChangedOn: dto.lastChangedOn,
    status: dto.status,
    cooperationClusters: [], // Not present in OpportunityDto, set as empty array
    sendFollowUpReminderOn: dto.sendFollowUpReminderOn,
    clusterMemberId: dto.clusterMemberId,
    assignedTo: dto.assignedTo,
    opportunityType: dto.opportunityType,
    numericId: dto.numericId ?? 0, // fallback to 0 if undefined
    deliveredServices: undefined, // Not present in OpportunityDto, set as undefined
  };
}

export interface ServicesProps {
  onDeliverService: (serviceId: string) => void;
}

type DialogType = 'recommend' | 'deliver' | null;

const Services = () => {
  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [selectedServices, setSelectedServices] = useState<OneWayInServiceDto[]>([]);
  const { formId } = useParams<PageParams>();
  const { oneWayInOpportunityById, updateOneWayInOpportunityStatus } = useOneWayIn(formId);
  const { cluster } = useCluster();
  const { user, isAadUser, displayName } = useUser();
  const { activeClusterServices } = useOneWayInServices(cluster?.id);
  const { t: tTableTitles } = useTranslation('tableTitles');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const history = useMemo(() => oneWayInOpportunityById?.data?.history || [], [oneWayInOpportunityById.data]);
  const updateDto = useMemo(() => {
    if (!oneWayInOpportunityById.data) return undefined;
    return createOwiUpdateDto(
      oneWayInOpportunityById.data.id,
      OneWayInStatus.Active,
      '',
      isAadUser ? undefined : user?.clusterUserId,
      displayName,
      undefined,
    );
  }, [oneWayInOpportunityById.data, user, isAadUser, displayName]);
  const selectedServiceIds = useMemo(() => selectedServices.map((s) => s.id), [selectedServices]);
  const columns = useServiceColumns(history);

  const onUpdateOneWayInOpportunityStatus = (updateDto: OneWayInOpportunityStatusCreateDto) =>
    updateOneWayInOpportunityStatus.mutateAsync(updateDto).then(() => {
      setSelectedServices([]);
    });

  const actions: DataTableV3Actions<OneWayInServiceDto> = {
    secondary: [
      {
        icon: { prefix: 'fasr', iconName: 'paper-plane' },
        label: tTableTitles('sendRecommendation'),
        onClick: () => {
          setDialogType('recommend');
        },
      },
      {
        icon: { prefix: 'fasr', iconName: 'hand-holding-heart' },
        label: tTableTitles('markAsRecommended'),
        onClick: () => {
          if (selectedServices.length === 0) {
            toast.error(tOneWayIn('services.recommend.error.chooseOneService'));
            return;
          }
          if (!updateDto) return;

          updateDto.recommendedServices = selectedServices.map((s) => s.id!);
          updateDto.description = 'Tjeneste(r) anbefalt';

          onUpdateOneWayInOpportunityStatus(updateDto)
            .then(() => toast.success(tOneWayIn('services.recommend.success')))
            .catch(() => toast.error(tOneWayIn('services.recommend.error')));
        },
      },
      {
        icon: { prefix: 'fasr', iconName: 'hands-holding-heart' },
        label: tTableTitles('markAsDelivered'),
        onClick: () => setDialogType('deliver'),
      },
    ],
  };
  return (
    <>
      {oneWayInOpportunityById?.data && (
        <>
          <RecommendServicesModal
            key={'recommend-' + selectedServiceIds.join()}
            dialogOpen={dialogType === 'recommend'}
            onClose={() => setDialogType(null)}
            opportunities={[mapOpportunityDtoToSimpleV2Dto(oneWayInOpportunityById.data)]}
            initialSelectedServiceIds={selectedServiceIds}
          />
          <DeliverServicesModal
            key={'deliver-' + selectedServiceIds.join()}
            dialogOpen={dialogType === 'deliver'}
            onClose={() => setDialogType(null)}
            opportunities={[mapOpportunityDtoToSimpleV2Dto(oneWayInOpportunityById.data)]}
            initialSelectedServiceIds={selectedServiceIds}
          />
        </>
      )}
      <DataTableV3
        id={DataTableV3Ids.OneWayIn.Services}
        actions={actions}
        data={activeClusterServices}
        columns={columns}
        disableExport
        disableColumnEditor
        translationFn={(key: string) => tTableTitles(key as any)}
        showRowsSelectedText
        hideFilterText
        disableGlobalFilter
        setSelectedRows={setSelectedServices}
      />
    </>
  );
};

export default Services;
